/** Emissions by month - the real allocated series (ADR-0110), drawn calmly and honestly.
 *
 * A hand-built SVG in the stack ADR-0090 chose: no chart library, tokens for every colour, the
 * brand green nowhere (a bar painted green would say that number is good news). Stacked by scope in
 * `--s1` / `--s2`, Scope 3 in `--info` only when it is assessed.
 *
 * **Every treatment below exists because a plainer chart would say something false**, and each has
 * a test that constructs the condition:
 *
 * - **A month with no records is an empty dashed slot labelled "no records"**, never a zero bar. A
 *   bar of height zero reads as "measured, and nil"; it is the same lie the Scope 3 card refuses.
 * - **A month that covers only part of a calendar month is hatched** and says "part" on the axis:
 *   its value is smaller because the window is, not because the company emitted less.
 * - **A month that has not ended has a dashed outline** and says "open": bills for it may not have
 *   arrived, so a low bar there is a gap in the data before it is a fall in the emissions.
 * - **A month resting on estimates (grade C or D) carries "≈"**, and the legend says how many
 *   kilograms rest on them.
 * - **An unassessed scope is never drawn**, and the legend says "not assessed" rather than leaving
 *   the reader to infer it from a missing colour.
 * - **A series that does not add up to the total is not drawn at all** (`monthlyModel.reconcile`).
 *
 * **The register link is the exact bucket.** The register's `month=YYYY-MM` filter selects by a
 * figure's *start* month and is not allocation-aware (ADR-0110, decision 7), so a bill running
 * 15 December to 14 January is listed under December there and contributes to both months here.
 * The link therefore sets the screen's *window* (`from` / `to`) to the bucket's dates, which the
 * register honours and which makes it ask `/figures` for exactly the records the month was
 * allocated from. The detail says so, in the words "records overlapping this month", and says that
 * the window has moved.
 *
 * Every bar is a keyboard-focusable button; "View as table" shows the same values from the same
 * model. The chart sits in a box that scrolls sideways at phone width, never the page.
 */

import { useId, useRef, useState } from "react";

import type { InventoryMonthly } from "../api/queries";
import { ReadFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { CHART, TICK_LABEL } from "../components/charts/chartTokens";
import { useChartWidth } from "../components/charts/useChartWidth";
import { quantity, shortDate, tonnes } from "../lib/format";
import styles from "./InventoryMonthly.module.css";
import { scaleFor, SCOPE_KEYS, shownTonnes } from "./monthlyModel";
import type { MonthlyModel, MonthView, ScopeKey } from "./monthlyModel";

const TOP = 26;
const PLOT = 200;
const LEFT = 56;
const RIGHT = 8;
const LABELS = 70;
const MIN_SLOT = 46;
const MAX_BAR = 40;
/** Tall enough to hold "no records" set on its side at the axis font size. */
const EMPTY_SLOT_HEIGHT = 78;

const SCOPE_FILL: Record<ScopeKey, string> = {
  "1": CHART.scope1,
  "2": CHART.scope2,
  // Information, not praise (`chartTokens`): the same colour the scope donut gives Scope 3.
  "3": CHART.complete,
};

interface ReadState {
  data: InventoryMonthly | undefined;
  isPending: boolean;
  error: unknown;
  refetch: () => void;
}

interface Props {
  read: ReadState;
  /** `modelOf(read.data, expectedKg)`, built by the caller because it also decides what the
   * Analytics note may claim. Undefined while the read has no data. */
  model: MonthlyModel | undefined;
  method: "location" | "market";
  /** Open the register with the screen's window set to these two dates. */
  onOpenMonth: (bucketStart: string, bucketEnd: string) => void;
  /** Whether the cards above are the total this must equal; changes only the footnote's words. */
  reconcilesToCards?: boolean;
}

function flagsOf(month: MonthView): string[] {
  const flags: string[] = [];
  if (month.recorded && !month.isFullMonth) flags.push("part");
  if (!month.hasEnded) flags.push("open");
  return flags;
}

/** What a screen reader hears for one bar - the same facts the detail and the table carry. */
function describe(month: MonthView): string {
  if (!month.recorded) {
    return `${month.label}: no records. Nothing was recorded for this month, which is not a reading of zero.`;
  }
  const parts = month.scopes.map((cell) =>
    cell.assessed ? `Scope ${cell.scope} ${shownTonnes(cell.kg)}` : `Scope ${cell.scope} not assessed`,
  );
  const notes: string[] = [];
  if (!month.isFullMonth) notes.push("part of a calendar month");
  if (!month.hasEnded) notes.push("not yet closed");
  if (month.hasEstimate) notes.push("includes estimates");
  return `${month.label}: ${shownTonnes(month.totalKg)} tCO₂e. ${parts.join(", ")} tCO₂e. ${month.figureCount} figures${notes.length ? `. ${notes.join(", ")}` : ""}. Press to see the detail.`;
}

function Hatch({ id }: { id: string }) {
  return (
    <pattern id={id} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
      <line x1="0" y1="0" x2="0" y2="6" stroke={CHART.surface} strokeWidth="2.6" />
    </pattern>
  );
}

export function MonthlyChart({ read, model, method, onOpenMonth, reconcilesToCards }: Props) {
  const basis = method === "market" ? "market-based" : "location-based";
  const sub = model
    ? `${shortDate(model.windowStart)} to ${shortDate(model.windowEnd)} · ${basis} Scope 2 · tCO₂e by calendar month`
    : `${basis} Scope 2 · tCO₂e by calendar month`;
  return (
    <Panel title="Emissions by month" sub={sub}>
      <div data-testid="inventory-monthly" data-state={stateOf(read, model)}>
        {read.error ? (
          // **A failed read says it failed and draws nothing.** Not an empty chart: a year with no
          // bars is a claim about the company, and a dropped request must not be able to make it.
          <ReadFailed of="The monthly series" error={read.error} onRetry={() => read.refetch()} />
        ) : read.isPending || !model ? (
          <div role="status" aria-busy="true" data-testid="inventory-monthly-loading">
            <Empty>Reading the monthly series…</Empty>
          </div>
        ) : model.months.length === 0 ? (
          <Empty>This reporting window does not reach any month the series can show.</Empty>
        ) : !model.reconciliation.ok ? (
          <p role="alert" className={styles.alert} data-testid="inventory-monthly-unreconciled">
            The monthly series is not shown because it does not add up to the window total
            {model.reconciliation.reason ? ` (${model.reconciliation.reason})` : ""}. Nothing is drawn
            rather than a chart that disagrees with the figures above it.
          </p>
        ) : model.recordedCount === 0 ? (
          <Empty>
            No records overlap any month of this window, so there is nothing to chart. That is not a
            reading of zero.
          </Empty>
        ) : (
          <Drawn model={model} onOpenMonth={onOpenMonth} reconcilesToCards={reconcilesToCards} />
        )}
      </div>
    </Panel>
  );
}

function stateOf(read: ReadState, model: MonthlyModel | undefined): string {
  if (read.error) return "failed";
  if (read.isPending || !model) return "loading";
  if (model.months.length === 0 || model.recordedCount === 0) return "empty";
  if (!model.reconciliation.ok) return "unreconciled";
  return "loaded";
}

function Drawn({
  model,
  onOpenMonth,
  reconcilesToCards,
}: {
  model: MonthlyModel;
  onOpenMonth: (bucketStart: string, bucketEnd: string) => void;
  reconcilesToCards?: boolean;
}) {
  const holder = useRef<HTMLDivElement>(null);
  const width = useChartWidth(holder);
  const hatchId = `hatch-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const [selected, setSelected] = useState<string | null>(null);
  const [tableOpen, setTableOpen] = useState(false);

  const { months } = model;
  const slot = Math.max(MIN_SLOT, Math.floor((width - LEFT - RIGHT) / months.length));
  const svgWidth = LEFT + RIGHT + slot * months.length;
  const barWidth = Math.min(MAX_BAR, Math.round(slot * 0.62));
  const scale = scaleFor(Math.max(0, ...months.map((month) => month.drawnTonnes)));
  const base = TOP + PLOT;
  const yOf = (t: number) => base - (t / scale.max) * PLOT;
  const current = months.find((month) => month.key === selected);
  const monthsKg = model.reconciliation.monthsKg ?? "0";

  const anyPartial = months.some((month) => month.recorded && !month.isFullMonth);
  const anyOpen = months.some((month) => !month.hasEnded);
  const anyEmpty = months.some((month) => !month.recorded);
  const anyApportioned = model.apportionedMonthCount > 0;

  return (
    <>
      <ul className={styles.legend} aria-label="Legend" data-testid="monthly-legend">
        {SCOPE_KEYS.map((scope) => (
          <li key={scope} className={styles.legendItem}>
            <span
              className={model.assessed[scope] ? styles.swatch : `${styles.swatch} ${styles.swatchOff}`}
              style={model.assessed[scope] ? { background: SCOPE_FILL[scope] } : undefined}
              aria-hidden="true"
            />
            {model.assessed[scope] ? `Scope ${scope}` : `Scope ${scope} · not assessed, not drawn`}
          </li>
        ))}
        {anyPartial ? (
          <li className={styles.legendItem}>
            <span className={`${styles.swatch} ${styles.swatchHatch}`} aria-hidden="true" />
            Hatched: part of a calendar month
          </li>
        ) : null}
        {anyOpen ? (
          <li className={styles.legendItem}>
            <span className={`${styles.swatch} ${styles.swatchOpen}`} aria-hidden="true" />
            Dashed outline: month not yet closed
          </li>
        ) : null}
        {anyEmpty ? (
          <li className={styles.legendItem}>
            <span className={`${styles.swatch} ${styles.swatchEmpty}`} aria-hidden="true" />
            Empty slot: no records
          </li>
        ) : null}
        {model.estimateMonthCount > 0 ? (
          <li className={styles.legendItem} data-testid="monthly-legend-estimates">
            <span className={styles.approx} aria-hidden="true">
              ≈
            </span>
            {tonnes(model.estimatedKg ?? "0")} t of the {tonnes(monthsKg)} t shown rests on estimates
            (grade C or D), in {model.estimateMonthCount}{" "}
            {model.estimateMonthCount === 1 ? "month" : "months"}
          </li>
        ) : null}
      </ul>

      <div className={styles.scroller} ref={holder} data-testid="monthly-scroller">
        <svg
          viewBox={`0 0 ${svgWidth} ${TOP + PLOT + LABELS}`}
          width={svgWidth}
          height={TOP + PLOT + LABELS}
          role="group"
          aria-label="Emissions by month, stacked by scope"
          className={styles.svg}
        >
          <defs>
            <Hatch id={hatchId} />
          </defs>
          <text x={LEFT - 8} y={TOP - 10} textAnchor="end" {...TICK_LABEL}>
            tCO₂e
          </text>
          {scale.ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={LEFT}
                x2={svgWidth - RIGHT}
                y1={yOf(tick)}
                y2={yOf(tick)}
                stroke={tick === 0 ? CHART.axis : CHART.grid}
                strokeWidth={1}
              />
              <text x={LEFT - 8} y={yOf(tick) + 4} textAnchor="end" className={styles.tick} {...TICK_LABEL}>
                {quantity(String(tick), scale.decimals)}
              </text>
            </g>
          ))}

          {months.map((month, index) => {
            const x0 = LEFT + index * slot;
            const cx = x0 + slot / 2;
            const bx = cx - barWidth / 2;
            const flags = flagsOf(month);
            let top = base;
            const segments = month.recorded
              ? month.scopes.flatMap((cell) => {
                  const t = Number(cell.kg) / 1000;
                  if (!cell.assessed || !(t > 0)) return [];
                  const height = (t / scale.max) * PLOT;
                  top -= height;
                  return [{ scope: cell.scope, y: top, height }];
                })
              : [];
            const stackTop = segments.length ? top : base;
            const isSelected = selected === month.key;
            const toggle = () => setSelected(isSelected ? null : month.key);
            return (
              <g
                key={month.key}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                aria-label={describe(month)}
                className={styles.slot}
                data-month={month.key}
                data-state={month.recorded ? "recorded" : "no_records"}
                data-full-month={month.isFullMonth ? "true" : "false"}
                data-ended={month.hasEnded ? "true" : "false"}
                data-estimate={month.hasEstimate ? "true" : "false"}
                onClick={toggle}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggle();
                  } else if (event.key === "Escape") {
                    setSelected(null);
                  }
                }}
              >
                <rect
                  className={isSelected ? `${styles.hit} ${styles.hitOn}` : styles.hit}
                  x={x0 + 1}
                  y={TOP - 14}
                  width={slot - 2}
                  height={PLOT + LABELS + 10}
                  rx={4}
                />
                {month.recorded ? (
                  <>
                    {segments.map((segment) => (
                      <rect
                        key={segment.scope}
                        data-scope={segment.scope}
                        x={bx}
                        y={segment.y}
                        width={barWidth}
                        height={segment.height}
                        fill={SCOPE_FILL[segment.scope]}
                      />
                    ))}
                    {!month.isFullMonth && segments.length ? (
                      <rect
                        data-treatment="hatch"
                        x={bx}
                        y={stackTop}
                        width={barWidth}
                        height={base - stackTop}
                        fill={`url(#${hatchId})`}
                      />
                    ) : null}
                    {!month.hasEnded ? (
                      <rect
                        data-treatment="open"
                        x={bx}
                        y={Math.min(stackTop, base - 3)}
                        width={barWidth}
                        height={Math.max(3, base - stackTop)}
                        fill="none"
                        stroke={CHART.ink}
                        strokeWidth={1.4}
                        strokeDasharray="4 3"
                      />
                    ) : null}
                    {month.hasEstimate ? (
                      <text
                        data-treatment="estimate"
                        x={cx}
                        y={Math.min(stackTop, base - 3) - 6}
                        textAnchor="middle"
                        aria-hidden="true"
                        {...TICK_LABEL}
                        fill={CHART.ink}
                      >
                        ≈
                      </text>
                    ) : null}
                  </>
                ) : (
                  <g data-treatment="empty">
                    <rect
                      x={bx}
                      y={base - EMPTY_SLOT_HEIGHT}
                      width={barWidth}
                      height={EMPTY_SLOT_HEIGHT}
                      fill="none"
                      stroke={CHART.muted}
                      strokeWidth={1}
                      strokeDasharray="3 3"
                      rx={2}
                    />
                    <text
                      transform={`translate(${cx + 4},${base - 6}) rotate(-90)`}
                      {...TICK_LABEL}
                    >
                      no records
                    </text>
                  </g>
                )}
                <text x={cx} y={base + 16} textAnchor="middle" {...TICK_LABEL} fill={CHART.ink}>
                  {month.short}
                </text>
                {index === 0 || month.key.endsWith("-01") ? (
                  <text x={cx} y={base + 30} textAnchor="middle" {...TICK_LABEL}>
                    {month.year}
                  </text>
                ) : null}
                {flags.map((flag, row) => (
                  <text key={flag} x={cx} y={base + 44 + row * 13} textAnchor="middle" {...TICK_LABEL}>
                    {flag}
                  </text>
                ))}
              </g>
            );
          })}
        </svg>
      </div>

      <ul className={styles.notes} data-testid="monthly-notes">
        {anyPartial ? (
          <li data-note="part">
            Hatched months cover only part of a calendar month: the window starts or ends inside it,
            or an engagement limits it. Their bars are lower because less of the month is counted.
          </li>
        ) : null}
        {anyOpen ? (
          <li data-note="open">
            Months marked open have not finished yet. Bills for them may not have arrived, so a low
            bar there is missing data before it is lower emissions.
          </li>
        ) : null}
        {anyEmpty ? (
          <li data-note="empty">
            A dashed empty slot means no record overlaps that month. It is not a reading of zero.
          </li>
        ) : null}
        {anyApportioned ? (
          <li data-note="apportioned">
            A record longer than a month is split by calendar day, so a month holds a share of it.
            The register lists whole records.
          </li>
        ) : null}
      </ul>

      <p className={styles.reconcile} data-testid="monthly-reconcile">
        The {months.length} {months.length === 1 ? "month adds" : "months add"} up to{" "}
        <strong className="num">{tonnes(monthsKg)}</strong> tCO₂e
        {reconcilesToCards ? ", the total in the cards above" : ", the window total"}. Values are
        rounded for display; the sum is exact before rounding.
      </p>

      <div
        className={styles.detail}
        role="region"
        aria-label="Month detail"
        aria-live="polite"
        data-testid="monthly-detail"
      >
        {current ? (
          <Detail month={current} onOpenMonth={onOpenMonth} onClose={() => setSelected(null)} />
        ) : (
          <p className={styles.hint}>Select a month to see its figures and open the records behind it.</p>
        )}
      </div>

      <details
        className={styles.fallback}
        onToggle={(event) => setTableOpen(event.currentTarget.open)}
        data-testid="monthly-table-toggle"
      >
        <summary>View as table</summary>
        {tableOpen ? <MonthTable model={model} monthsKg={monthsKg} /> : null}
      </details>
    </>
  );
}

function Detail({
  month,
  onOpenMonth,
  onClose,
}: {
  month: MonthView;
  onOpenMonth: (bucketStart: string, bucketEnd: string) => void;
  onClose: () => void;
}) {
  return (
    <div data-testid="monthly-detail-body" data-month={month.key}>
      <div className={styles.detailHead}>
        <h3 className={styles.detailTitle}>{month.label}</h3>
        <button type="button" className={styles.close} onClick={onClose}>
          Close
        </button>
      </div>
      <p className={styles.detailLine}>Covers {month.covers}.</p>
      {!month.recorded ? (
        <p className={styles.detailLine} data-testid="monthly-detail-empty">
          No records overlap this month. Nothing was recorded for it, which is not a reading of zero.
        </p>
      ) : (
        <>
          <dl className={styles.values}>
            {month.scopes.map((cell) => (
              <div key={cell.scope} className={styles.valueRow}>
                <dt>Scope {cell.scope}</dt>
                <dd className="num">
                  {cell.assessed ? `${shownTonnes(cell.kg)} tCO₂e` : "Not assessed"}
                </dd>
              </div>
            ))}
            <div className={`${styles.valueRow} ${styles.valueTotal}`}>
              <dt>Month total</dt>
              <dd className="num">{shownTonnes(month.totalKg)} tCO₂e</dd>
            </div>
          </dl>
          <p className={styles.detailLine}>
            {month.figureCount} source {month.figureCount === 1 ? "figure overlaps" : "figures overlap"} this
            month
            {month.apportionedFigureCount > 0
              ? `; ${month.apportionedFigureCount} of them ${month.apportionedFigureCount === 1 ? "is a record" : "are records"} longer than the month, so this month holds a calendar-day share`
              : ""}
            .
          </p>
          {!month.isFullMonth ? (
            <p className={styles.detailLine} data-testid="monthly-detail-part">
              Part of a calendar month: only {month.covers} is counted here.
            </p>
          ) : null}
          {!month.hasEnded ? (
            <p className={styles.detailLine} data-testid="monthly-detail-open">
              This month has not finished. Bills for it may not have arrived yet.
            </p>
          ) : null}
          {month.hasEstimate ? (
            <p className={styles.detailLine} data-testid="monthly-detail-estimate">
              {month.estimateFigureCount} {month.estimateFigureCount === 1 ? "figure rests" : "figures rest"} on
              estimates (grade C or D): {tonnes(month.estimatedKg)} t CO₂e
              {month.estimateShare ? `, ${month.estimateShare} of this month` : ""}.
            </p>
          ) : (
            <p className={styles.detailLine}>No estimates in this month.</p>
          )}
          <button
            type="button"
            className={styles.open}
            onClick={() => onOpenMonth(month.bucketStart, month.bucketEnd)}
          >
            Open the register for {month.covers}
          </button>
          <p className={styles.fine}>
            Lists the records overlapping this month, as whole records rather than the calendar-day
            shares drawn here. It sets this screen&rsquo;s window to those dates; choose the period
            above to come back.
          </p>
        </>
      )}
    </div>
  );
}

function MonthTable({ model, monthsKg }: { model: MonthlyModel; monthsKg: string }) {
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table} data-testid="monthly-table">
        <caption>
          Emissions by month, tCO₂e, {shortDate(model.windowStart)} to {shortDate(model.windowEnd)}
        </caption>
        <thead>
          <tr>
            <th scope="col">Month</th>
            <th scope="col">Covers</th>
            {SCOPE_KEYS.map((scope) => (
              <th key={scope} scope="col">
                Scope {scope}
              </th>
            ))}
            <th scope="col">Month total</th>
            <th scope="col">Figures</th>
            <th scope="col">On estimates (tCO₂e)</th>
            <th scope="col">Notes</th>
          </tr>
        </thead>
        <tbody>
          {model.months.map((month) => {
            const notes: string[] = [];
            if (!month.recorded) notes.push("No records");
            if (month.recorded && !month.isFullMonth) notes.push("Part of a calendar month");
            if (!month.hasEnded) notes.push("Not yet closed");
            if (month.hasEstimate) notes.push("Includes estimates");
            return (
              <tr key={month.key} data-month={month.key}>
                <th scope="row">{month.label}</th>
                <td>{month.covers}</td>
                {month.scopes.map((cell) => (
                  <td key={cell.scope} className={styles.n}>
                    {!cell.assessed ? "Not assessed" : month.recorded ? shownTonnes(cell.kg) : "No records"}
                  </td>
                ))}
                <td className={styles.n}>{month.recorded ? shownTonnes(month.totalKg) : "No records"}</td>
                <td className={styles.n}>{month.recorded ? month.figureCount : "—"}</td>
                <td className={styles.n}>
                  {month.recorded && month.hasEstimate ? tonnes(month.estimatedKg) : "—"}
                </td>
                <td>{notes.join("; ") || "—"}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" colSpan={2}>
              Total of the months
            </th>
            <td colSpan={SCOPE_KEYS.length} />
            <td className={styles.n} data-testid="monthly-table-total">
              {tonnes(monthsKg)}
            </td>
            <td colSpan={3} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
